import React from 'react';
import { Card, Form, Button, Alert, Row, Col, Spinner } from 'react-bootstrap';
import { TestSuiteConfig, SuiteProgress } from './types';
import { DEFAULT_BATCH_SIZES, DEFAULT_BATCH_INTERVALS } from './constants';
import { useTranslation } from 'react-i18next';

interface TestSuiteConfigurationProps {
  testSuiteConfig: TestSuiteConfig;
  isRunningSuite: boolean;
  isRunningTest: boolean;
  loading: boolean;
  hasSelectedCounter: boolean;
  currentSuiteProgress: SuiteProgress | null;
  handleTestSuiteConfigChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  runTestSuite: () => void;
  runAllTestSuite: () => void;
  cancelTestSuite: () => void;
  getTestCaseCount: () => number;
  getEstimatedTime: () => number;
}

const TestSuiteConfiguration: React.FC<TestSuiteConfigurationProps> = ({
  testSuiteConfig,
  isRunningSuite,
  isRunningTest,
  loading,
  hasSelectedCounter,
  currentSuiteProgress,
  handleTestSuiteConfigChange,
  runTestSuite,
  runAllTestSuite,
  cancelTestSuite,
  getTestCaseCount,
  getEstimatedTime
}) => {
  const { t } = useTranslation();

  return (
    <Card>
      <Card.Header className="bg-primary text-white">
        <h2 className="h5 mb-0">Test Suite Configuration</h2>
      </Card.Header>
      <Card.Body>
        <p className="text-muted mb-3">
          Run performance tests with all combinations of selected batch sizes and intervals.
          Each test automatically resets tap counts before running to maintain a consistent baseline.
          {getTestCaseCount() > 0 && (
            <span> The test suite will run <strong>{getTestCaseCount()}</strong> total test cases (est. {getEstimatedTime()} seconds).</span>
          )}
        </p>
        
        {currentSuiteProgress && (
          <Alert variant={currentSuiteProgress.isCooldown ? "warning" : "info"}>
            <div className="d-flex justify-content-between align-items-center">
              <div>
                {currentSuiteProgress.isCooldown ? (
                  <strong>Cooldown period (1s) before next test...</strong>
                ) : (
                  <strong>Running test {currentSuiteProgress.current} of {currentSuiteProgress.total}</strong>
                )}
                <div>
                  {currentSuiteProgress.isCooldown ? 
                    `Next test: Batch size: ${currentSuiteProgress.batchSize}, Interval: ${currentSuiteProgress.interval}ms` :
                    `Batch size: ${currentSuiteProgress.batchSize}, Interval: ${currentSuiteProgress.interval}ms`
                  }
                </div>
              </div>
              <Button variant="danger" size="sm" onClick={cancelTestSuite}>
                Cancel Suite
              </Button>
            </div>
            <div className="progress mt-2">
              <div 
                className="progress-bar" 
                role="progressbar" 
                style={{ width: `${currentSuiteProgress.progress || (currentSuiteProgress.current / currentSuiteProgress.total) * 100}%` }}
              >
                {Math.round(currentSuiteProgress.progress || (currentSuiteProgress.current / currentSuiteProgress.total) * 100)}%
              </div>
            </div>
          </Alert>
        )}
        
        <Form>
          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label><strong>Batch Sizes</strong></Form.Label>
                <div className="d-flex flex-wrap gap-2">
                  {DEFAULT_BATCH_SIZES.map(size => (
                    <Form.Check
                      key={`batchSize-${size}`}
                      type="checkbox"
                      id={`batchSize-${size}`}
                      name={`batchSize-${size}`}
                      label={size}
                      checked={testSuiteConfig.selectedBatchSizes.includes(size)}
                      onChange={handleTestSuiteConfigChange}
                      disabled={isRunningSuite || isRunningTest}
                    />
                  ))}
                </div>
              </Form.Group>
            </Col>
            
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label><strong>Batch Intervals (ms)</strong></Form.Label>
                <div className="d-flex flex-wrap gap-2">
                  {DEFAULT_BATCH_INTERVALS.map(interval => (
                    <Form.Check
                      key={`interval-${interval}`}
                      type="checkbox"
                      id={`interval-${interval}`}
                      name={`interval-${interval}`}
                      label={interval}
                      checked={testSuiteConfig.selectedIntervals.includes(interval)}
                      onChange={handleTestSuiteConfigChange}
                      disabled={isRunningSuite || isRunningTest}
                    />
                  ))}
                </div>
              </Form.Group>
            </Col>
          </Row>
          
          <div className="mt-3 d-flex justify-content-between">
            <div>
              {isRunningSuite ? (
                <Button 
                  variant="warning" 
                  onClick={cancelTestSuite}
                >
                  <i className="bi bi-x-circle me-1"></i>
                  {t('controls.cancel', 'Cancel')}
                </Button>
              ) : (
                <>
                  <Button 
                    variant="primary" 
                    onClick={() => runTestSuite()}
                    disabled={!hasSelectedCounter || isRunningTest || isRunningSuite || 
                      testSuiteConfig.selectedBatchSizes.length === 0 || 
                      testSuiteConfig.selectedIntervals.length === 0}
                    className="me-2"
                  >
                    <i className="bi bi-play-fill me-1"></i>
                    {t('controls.runSelected', 'Run Selected')}
                  </Button>
                  
                  <Button 
                    variant="outline-primary" 
                    onClick={() => runAllTestSuite()}
                    disabled={!hasSelectedCounter || isRunningTest || isRunningSuite}
                  >
                    <i className="bi bi-play-fill me-1"></i>
                    {t('controls.runAll', 'Run All')}
                  </Button>
                </>
              )}
            </div>
          </div>
          <div className="text-muted mt-2 small">
            Uses the counter selection, clicks per second, and duration settings from above.
            All selected combinations will be tested sequentially with a 1 second cooldown between each test.
          </div>
          
          {getTestCaseCount() > 20 && (
            <Alert variant="warning" className="mt-3">
              <strong>Performance Warning:</strong> Running {getTestCaseCount()} tests may take a significant amount of time. 
              Consider selecting fewer combinations for faster results.
            </Alert>
          )}
        </Form>
      </Card.Body>
    </Card>
  );
};

export default TestSuiteConfiguration; 